import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster, toast } from 'sonner';
import { TooltipProvider } from '@/shared/ui/tooltip';
import { App } from '@/app/App';
import { AuthGate } from '@/app/AuthGate';
import { PublicShareView, publicShareToken } from '@/features/public-share/PublicShareView';
import { PublicSketchupClientView, publicSketchupClientRoute } from '@/features/public-share/PublicSketchupClientView';
import { queryClient } from '@/app/query-client';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { ThemeBridge } from '@/shared/ui/theme-bridge';
import { useUiStore } from '@/shared/store/ui-store';
import { useViewerStore } from '@/shared/store/viewer-store';
import { installTouchDnDBridge } from '@/shared/lib/touch-dnd';
import { installTelemetry } from '@/shared/telemetry';
import { installAuthFetchInterceptor } from '@/shared/api/auth-fetch';
import { shouldReloadForServiceWorker } from '@/shared/service-worker-update';
import '@/styles/globals.css';
// Строго после globals.css: правило читаемости отключённых контролов должно
// стоять позже утилит Tailwind. См. шапку файла — там замер.
import '@/styles/disabled-legibility.css';
import { ExclusiveWorkspaceRouteOwner } from '@/app/exclusive-workspace-route';

const EventWorkspace = React.lazy(() => import('@/features/event-location/EventWorkspace'));
const BuildingWorkspace = React.lazy(() => import('@/features/buildings/BuildingWorkspace'));
const RecognitionArmWorkspace = React.lazy(() => import('@/features/recognition-arm/RecognitionArmWorkspace'));
const MeshStudioWorkspace = React.lazy(() => import('@/features/mesh-studio/MeshStudioModelEntry'));

// Polyfill HTML5 drag-and-drop on touch devices.  No-op on desktop.
installTouchDnDBridge();

// Разблокировка междоменного CSS Inter (index.html держит его на media="print",
// чтобы не блокировать рендер). Подмена media — по событию load из module-скрипта,
// не из inline-onload: CSP script-src запрещает обработчики в разметке. Ранняя
// подмена вернула бы блокировку рендера; офлайн-случай молча остаётся на фолбэке.
const interCss = document.getElementById('inter-css');
if (interCss) {
  const unlock = () => interCss.setAttribute('media', 'all');
  // Кеш-случай: лист уже загружен к моменту запуска модуля — событие не придёт.
  if ((interCss as HTMLLinkElement).sheet) unlock();
  else {
    interCss.addEventListener('load', unlock, { once: true });
    interCss.addEventListener('error', () => interCss.remove(), { once: true });
  }
}

// Must be installed before AuthGate/App issue requests so even legacy raw
// fetch('/api/...') call sites participate in session-expiry handling.
installAuthFetchInterceptor();

window.addEventListener('holzcom-bundle-conflict', (event) => {
  const path = (event as CustomEvent<{ path?: string }>).detail?.path ?? '';
  const match = path.match(/^\/api\/models\/([^/]+)/);
  const modelId = match ? decodeURIComponent(match[1]!) : null;
  if (modelId) {
    void queryClient.invalidateQueries({ queryKey: ['bundle', modelId] });
    void queryClient.invalidateQueries({ queryKey: ['history', modelId] });
  }
  toast.error('Проект изменён в другой вкладке', {
    description: 'Данные обновляются. Повторите действие после синхронизации.',
  });
});

// (#13) Capture unhandled errors → POST /api/system/client-errors.  Production only.
installTelemetry();

// (#11) Register service worker for offline shell + static caching.
// Disabled in dev (Vite hot reload) to avoid stale chunk loads.
// Detected via Vite's MODE — only register on production builds.
declare const __HOLZCOM_PROD__: boolean;
if (typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof __HOLZCOM_PROD__ !== 'undefined' && __HOLZCOM_PROD__) {
  window.addEventListener('load', () => {
    let reloadingForSwUpdate = false;
    let swReloadPending = false;
    let moduleDraftDirty = false;
    const reloadWhenSaved = () => {
      if (!swReloadPending || reloadingForSwUpdate) return;
      const status = useViewerStore.getState().moduleSaveStatus;
      if (!shouldReloadForServiceWorker(status, moduleDraftDirty)) return;
      reloadingForSwUpdate = true;
      window.location.reload();
    };
    window.addEventListener('holzcom:module-draft-dirty', (event) => {
      moduleDraftDirty = (event as CustomEvent<{ dirty?: boolean }>).detail?.dirty === true;
      reloadWhenSaved();
    });
    useViewerStore.subscribe(reloadWhenSaved);
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      swReloadPending = true;
      reloadWhenSaved();
    });
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
        registration.addEventListener('updatefound', () => {
          const worker = registration.installing;
          worker?.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) {
              worker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
        return registration.update();
      })
      .catch((err) => {
        console.warn('SW registration failed:', err);
      });
  });
}

// (#16 round-7) Pre-warm the SceneCanvas chunk during idle time so
// the first switch into 3D mode doesn't pay the 500 KB+ fetch.  Uses
// requestIdleCallback when available — otherwise a small setTimeout.
const _preloadSceneCanvas = () => { void import('@/features/scene/SceneCanvas'); };
if (typeof window !== 'undefined') {
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
  if (typeof ric === 'function') ric(_preloadSceneCanvas, { timeout: 4000 });
  else setTimeout(_preloadSceneCanvas, 1500);
}

// (F3) IndexedDB persistence for react-query — bundle dict survives
// page reload so cold-load is instant when the underlying data hasn't
// changed.  Cache busted automatically by bundleHash from SSE events.
function ToasterBridge() {
  const theme = useUiStore((s) => s.theme);
  return (
    <Toaster
      position="bottom-right"
      theme={theme}
      richColors
      closeButton
      toastOptions={{
        style: {
          background: 'rgb(var(--surface))',
          color: 'rgb(var(--fg))',
          border: '1px solid rgb(var(--border))',
        },
      }}
    />
  );
}

// HMR-safe root: cache the Root on window so subsequent dev reloads call
// `.render()` on the existing root instead of `createRoot()` again. Without
// this, every Vite HMR fired the "calling createRoot() on a container that
// has already been passed" warning (often dozens of times per session).
// Protected query persistence is owned by AuthGate and starts only after a
// verified session. No project cache is restored into an unauthenticated UI.

const rootEl = document.getElementById('root')!;
const w = window as unknown as { __holzcomRoot?: ReturnType<typeof ReactDOM.createRoot> };
const root = w.__holzcomRoot ?? ReactDOM.createRoot(rootEl);
w.__holzcomRoot = root;
// hydrateDecorScans() переехал в AuthGate.markAuthed: звать его до логина =
// гарантированный 401 /api/materials/decor-scans на экране входа (аудит 10.10).

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200} skipDelayDuration={0}>
        <ThemeBridge />
        <ErrorBoundary label="app-root">
          {publicSketchupClientRoute() ? <PublicSketchupClientView /> : publicShareToken() ? <PublicShareView /> : (
            <AuthGate>
              <ExclusiveWorkspaceRouteOwner
                recognition={
                  <React.Suspense fallback={<div className="grid h-screen place-items-center bg-bg text-fg">Загрузка ARM…</div>}>
                    <RecognitionArmWorkspace />
                  </React.Suspense>
                }
                meshStudio={
                  <React.Suspense fallback={<div className="grid h-screen place-items-center bg-[var(--mesh-ground-2)] text-[color:var(--mesh-accent)]">Загрузка Mesh Studio…</div>}>
                    <MeshStudioWorkspace />
                  </React.Suspense>
                }
                eventLocation={<React.Suspense fallback={<div>Загрузка локации…</div>}><EventWorkspace /></React.Suspense>}
                buildings={<React.Suspense fallback={<div>Загрузка построек…</div>}><BuildingWorkspace /></React.Suspense>}
                scene={<App />}
              />
            </AuthGate>
          )}
        </ErrorBoundary>
        <ToasterBridge />
      </TooltipProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
